import React, { ChangeEvent, useEffect, useState } from "react";
import {
  createDevice,
  deleteDevice,
  updateDevice,
  getDeviceById,
} from "../../api/devices/deviceApi";
import { getInstallations } from "../../api/installations/installationApi";
import { toast } from "react-toastify";
import { MoonLoader } from "react-spinners";
import { useNavigate, useParams } from "react-router-dom";
import { Device, Installation, Organization } from "../common/ObjectTypes";
import AddEditDevice from "./AddEditDevice";
import { getOrganizations } from "../../api/organizations/organizationsApi";
import { getCurrentUser } from "../../helpers/authHeader";

type ManageAddEditDeviceProps = {
  existingDevice: boolean;
};

const ManageAddEditDevice = ({ existingDevice }: ManageAddEditDeviceProps) => {
  const { v4: uuidv4 } = require("uuid");
  const currentUser = getCurrentUser();
  const [pageLoaded, setPageLoaded] = useState(false);
  const navigate = useNavigate();
  const [addDevice, setAddDevice] = useState(false);
  const { id: deviceId } = useParams<{ id: string }>(); // Extract the `id` parameter
  const [errors, setErrors] = useState<any>({});
  const [device, setDevice] = useState<Device>({
    name: "",
    deviceId: "",
    installationId: "",
    wildXRNumber: "",
    organizationId: "",
    rowVersion: "",
  });
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  useEffect(() => {
    const loadAllData = async () => {
      try {
        // Fetch all necessary data in parallel
        const [fetchedInstallations, fetchedOrganizations, fetchedDevice] =
          await Promise.all([
            getInstallations(1000),
            getOrganizations(),
            existingDevice && deviceId
              ? getDeviceById(deviceId)
              : Promise.resolve(null),
          ]);

        // Set the fetched data to state
        setInstallations(fetchedInstallations);
        setOrganizations(fetchedOrganizations);
        if (fetchedDevice) {
          setDevice(fetchedDevice);
        }

        // Mark the page as loaded only after all data is fetched
        setPageLoaded(true);
      } catch (err) {
        console.error(err); // Log the error for debugging
      }
    };

    if (!pageLoaded) {
      loadAllData();
    }
  }, [pageLoaded, existingDevice, deviceId]);

  const handleSelectDevice = (device: Device) => {
    setDevice({
      name: device.name,
      deviceId: device.deviceId,
      installationId: device.installationId,
      wildXRNumber: device.wildXRNumber,
      organizationId: device.organizationId,
      rowVersion: device.rowVersion,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setDevice((prev: Device) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeSingleSelect = (name: string, e: any) => {
    const newValue = e === null ? null : e.value;
    setDevice((prevDevice: any) => ({
      ...prevDevice,
      [name]: newValue,
    }));
  };

  const formIsValid = (): boolean => {
    const newErrors: any = {};
    if (!device.name) {
      newErrors.name = "Device name is required";
    }

    if (!device.wildXRNumber) {
      newErrors.wildXRNumber = "Serial number is required";
    }

    if (
      !device.organizationId &&
      !currentUser.organizations[0].organizationId
    ) {
      newErrors.organizationId = "Organization is required";
    }
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e: any) => {
    e.preventDefault();
    const updatedDevice = {
      ...device,
      deviceId: device.deviceId || uuidv4(),
      organizationId: device.organizationId
        ? device.organizationId
        : currentUser.organizations[0].organizationId,
    };
    try {
      if (!formIsValid()) return;
      if (!existingDevice) {
        await createDevice(updatedDevice);
      } else {
        await updateDevice(updatedDevice);
      }
      toast.success(device.name + (existingDevice ? " Updated" : " Added."), {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      navigate("/devices");
      setAddDevice(false);
      setPageLoaded(false);
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setErrors({ onSave: error.response.data.message });
      } else {
        setErrors({ onSave: "An unexpected error occurred." });
      }
    }
  };

  const handleCancelEdit = () => {
    setDevice({
      name: "",
      deviceId: "",
      installationId: "",
      wildXRNumber: "",
      organizationId: "",
      rowVersion: "",
    });
    navigate("/devices");
  };

  const handleDelete = async () => {
    try {
      await deleteDevice(device.deviceId ? device.deviceId : "");
      setDevice({
        name: "",
        deviceId: "",
        installationId: "",
        wildXRNumber: "",
        organizationId: "",
        rowVersion: "",
      });
      toast.success("Device was deleted.", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      setPageLoaded(false);
    } catch (error) {
      console.log(error);
    }
  };
  return !pageLoaded ? (
    <div className="flex justify-center items-center">
      <MoonLoader color="#9EB45B" />
    </div>
  ) : (
    <div className="">
      <AddEditDevice
        device={device}
        installations={installations}
        organizations={organizations}
        errors={errors}
        addDevice={addDevice}
        onSelectDevice={handleSelectDevice}
        onCancelEdit={handleCancelEdit}
        onChange={handleChange}
        onSave={handleSave}
        onChangeSingleSelect={handleChangeSingleSelect}
        onDelete={handleDelete}
        existingDevice={existingDevice}
      />
    </div>
  );
};

export default ManageAddEditDevice;
